import { React, useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate } from "react-router";
import { useForm } from "react-hook-form";
import { useDropzone } from "react-dropzone";

import { Button } from "../../commonComponents/Buttons";
import InputFields from "../../commonComponents/InputFields/InputFields";
import { UploadedModal } from "../../modals";

import challengesService from "../../../services/challengesService";

import { UploadFileImg, UploadFileError } from "../../../assets";
import { useDispatch } from "react-redux";
import { get_challenge_info } from "../../../redux/features/challengeSlice";

function ChallengeUpload() {
  const navigate = useNavigate();
  const params = useParams();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  
  const [uploadedModal, setUploadedModal] = useState(false);
  const [videoUploadPercentage, setVideoUploadPercentage] = useState(0);
  const [file, setFile] = useState(null);
  const [formatError, setFormatError] = useState(false);
  const [sizeError, setSizeError] = useState(false);
  const [uploadType, setUploadType] = useState(null);
  const [allowedUploadTypes, setAllowedUploadTypes] = useState([]);

  const [fileAccept, setFileAccept] = useState();
  const [fileMaxSize, setFileMaxSize] = useState();

  function getFileType(uploadedFile) {
    if (!uploadedFile) {
      return null;
    }
  
    if (uploadedFile.type.startsWith("video/")) {
      return "video";
    }
  
    if (uploadedFile.type === "application/pdf") {
      return "pdf";
    }
  
    if (uploadedFile.type.startsWith("image/")) {
      return "image";
    }
  
    return null;
  }
  
  const selectedFileType = getFileType(file);
  
  const hasFileUpload =
    allowedUploadTypes.includes("video") ||
    allowedUploadTypes.includes("pdf") ||
    allowedUploadTypes.includes("image");

  useEffect(() => {
    const args = {
        categoryId: params.categoryId,
        challengeId: params.challengeId,
    };

    dispatch(get_challenge_info(args)).then((result) => {
        const allowedTypes = result?.payload?.allowed_answer_types || [];

        setAllowedUploadTypes(allowedTypes);

        if (params.uploadType && allowedTypes.includes(params.uploadType)) {
            setUploadType(params.uploadType);
        } else {
            setUploadType(allowedTypes[0] || null);
        }
    });
  }, [dispatch, params.categoryId, params.challengeId, params.uploadType]);

  useEffect(() => {
    const accept = {};

    if (allowedUploadTypes.includes("image")) {
      accept["image/jpeg"] = [];
      accept["image/png"] = [];
      accept["image/jpg"] = [];
    }

    if (allowedUploadTypes.includes("pdf")) {
      accept["application/pdf"] = [];
    }

    if (allowedUploadTypes.includes("video")) {
      accept["video/mp4"] = [];
      accept["video/quicktime"] = [];
      accept["video/avi"] = [];
      accept["video/webm"] = [];
    }

    setFileAccept(accept);

    if (allowedUploadTypes.includes("video")) {
      setFileMaxSize(524288001);
    } else {
      setFileMaxSize(104857601);
    }
  }, [allowedUploadTypes]);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    criteriaMode: "all",
    defaultValues: {
      challenge_title: "",
      text_answer: "",
    },
  });

  const onDrop = useCallback((acceptedFiles) => {
    const uploadedFile = acceptedFiles[0];

    if (!uploadedFile) {
      return;
    }

    const detectedType = getFileType(uploadedFile);

    if (!detectedType || !allowedUploadTypes.includes(detectedType)) {
      setFormatError(true);
      setFile(null);
      return;
    }

    setFormatError(false);
    setFile(uploadedFile);
  }, [allowedUploadTypes]);

  const { fileRejections, getRootProps, getInputProps } = useDropzone({
    onDrop,
    maxFiles: 1,
    maxSize: fileMaxSize,
    accept: fileAccept,
  });

  function upload_file(start, path, data) {
    setUploadedModal(true);
    let max_length = 1024 * 1024 * 15;
    let end;
    let existingPath = path;
    let formData = new FormData();
    let nextChunk = start + max_length + 1;
    let currentChunk = file.slice(start, nextChunk);
    let uploadedChunk = start + currentChunk.size;
    if (uploadedChunk >= file.size) {
      end = 1;
    } else {
      end = 0;
    }

    formData.append("video_chunk", currentChunk);
    formData.append("end", end);
    formData.append("existingPath", existingPath);
    formData.append("nextSlice", nextChunk);
    formData.append("title", data?.challenge_title);
    formData.append("size", file.size);

    challengesService
      .postNewChallengesFile(params.categoryId, params.challengeId, formData)
      .then((res) => {
        if (nextChunk < file.size) {
          existingPath = res.data.existingPath;
          upload_file(nextChunk, existingPath, data);

          setVideoUploadPercentage(
            Math.round((uploadedChunk / file.size) * 100)
          );
        } else {
          if (res.data.result === 'created') {
            setVideoUploadPercentage(100);
          }
        }
      });
  }

  function checkAllData(data) {
    if (!data) {
      return;
    }

    if (file) {
      const detectedType = getFileType(file);

      if (detectedType === "video") {
        upload_file(0, null, data);
        return;
      }

      challengesService
        .postNewChallengesFile(
          params.categoryId,
          params.challengeId,
          data,
          file
        )
        .then((response) => {
          if (response) {
            setUploadedModal(true);
          }
        });

      return;
    }

    if (allowedUploadTypes.includes("text") && data?.text_answer) {
      challengesService
        .postNewChallengesFile(
          params.categoryId,
          params.challengeId,
          data
        )
        .then((response) => {
          if (response) {
            setUploadedModal(true);
          }
        });
    }
  }

  useEffect(() => {
    if (fileRejections[0]?.errors[0]?.code === "file-invalid-type") {
      setFormatError(true);
    } else {
      setFormatError(false);
    }

    if (fileRejections[0]?.errors[0]?.code === "file-too-large") {
      setSizeError(true);
    } else {
      setSizeError(false);
    }
  }, [formatError, sizeError, fileRejections]);

  function renderInfo() {
    const formats = [];

    if (allowedUploadTypes.includes("video")) {
      formats.push("mp4, mov, qt, avi, webm");
    }

    if (allowedUploadTypes.includes("pdf")) {
      formats.push("PDF");
    }

    if (allowedUploadTypes.includes("image")) {
      formats.push("jpg, jpeg, png");
    }

    const allowedFormats = formats.join(", ");

    if (formatError) {
      return (
        <>
          <p>
            Unsupported file format.{" "}
            <span className="linkColor">{t("tryAgain")}</span>
          </p>
          <p className="reqTxt">
            <span>Allowed file formats: {allowedFormats}.</span>
          </p>
        </>
      );
    }

    if (sizeError) {
      return (
        <>
          <p>
            File is too large.{" "}c
            <span className="linkColor">{t("tryAgain")}</span>
          </p>
          <p className="reqTxt">
            {allowedUploadTypes.includes("video") && (
              <span>Videos: maximum 500 MB. </span>
            )}
            {(allowedUploadTypes.includes("pdf") || allowedUploadTypes.includes("image")) && (
              <span>PDFs and images: maximum 100 MB.</span>
            )}
          </p>
        </>
      );
    }

    return (
      <>
        <p>{t("dragDrop")}</p>
        <p>
          {t("dragDropExplain")}{" "}
          <span className="linkColor">{t("browseManually")}</span>.
        </p>
        <p className="reqTxt">
          <span>Allowed file formats: {allowedFormats}.</span>{" "}
          {allowedUploadTypes.includes("video") && (
            <span>Videos: maximum 500 MB. </span>
          )}
          {(allowedUploadTypes.includes("pdf") || allowedUploadTypes.includes("image")) && (
            <span>PDFs and images: maximum 100 MB.</span>
          )}
        </p>
      </>
    );
  }

  function removeVideo() {
    setFile(null);
  }

  return (
    <main>
      <Button
        label={t("back")}
        iconClassName="hasIcon--chevron-left"
        viewType="backButton"
        onClick={() => navigate(-1)}
      />
      <section className="uploadBlock">
        <h2>{t("addChallenge")}</h2>
        <legend>{t("addChallengeLegend")}</legend>
        <form onSubmit={handleSubmit(checkAllData)}>
          <fieldset>
            <ul className="formFieldsList">
              <li>
                <InputFields
                  errors={errors}
                  inputType="inputName"
                  register={register}
                  fieldName="challenge_title"
                  lableName={t("submissionLabel")}
                />
              </li>
              {allowedUploadTypes.includes("text") && !file && (
                <li>
                  <InputFields
                    errors={errors}
                    inputType="textArea"
                    register={register}
                    fieldName="text_answer"
                    lableName="Text"
                  />
                </li>
              )}

              {hasFileUpload && (
                <li>
                  {file ? (
                    <div className="challengePreviewBlock">
                      <Button
                        iconClassName="hasIcon--close"
                        viewType="transparentIcon"
                        customClassName="removeVideo"
                        onClick={() => removeVideo()}
                      />

                      {selectedFileType === "video" && (
                        <video
                          preload="metadata"
                          className="challengePreview"
                          src={URL.createObjectURL(file)}
                        ></video>
                      )}

                      {selectedFileType === "image" && (
                        <img
                          alt={t("Upload")}
                          className="challengePreview image"
                          src={URL.createObjectURL(file)}
                        />
                      )}

                      {selectedFileType === "pdf" && (
                        <embed
                          className="challengePreview pdf"
                          src={URL.createObjectURL(file)}
                          type="application/pdf"
                        />
                      )}
                    </div>
                  ) : (
                    <section className={`dragDropZone ${formatError || sizeError ? "errorZone" : undefined}`}>
                      <div {...getRootProps()}>
                        <input {...getInputProps()} />
                        <img
                          src={formatError || sizeError ? UploadFileError : UploadFileImg}
                          alt={t("Upload")}
                        />
                        {renderInfo()}
                      </div>
                    </section>
                  )}
                </li>
              )}
              {(file || allowedUploadTypes.includes("text")) && (
                <li>
                  <Button
                    type="submit"
                    label={t("addChallenge")}
                    viewType="mainBtn"
                  />
                </li>
              )}
            </ul>
          </fieldset>
        </form>
      </section>

      {uploadedModal && (
        <UploadedModal
          uploadType={uploadType}
          percentage={videoUploadPercentage}
        />
      )}
    </main>
  );
}

export default ChallengeUpload;
